import { AlertCircle, Loader2 } from 'lucide-react'
import { apiErrorMessage } from '@/lib/apiErrors'
import type { LiveRepointResult } from '@/types/bundle'
import { Banner } from './Banner'
import { Modal } from './Modal'

export interface GoLiveModalProps {
  open: boolean
  targetVersion?: string
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
  isError: boolean
  error: unknown
  isSuccess: boolean
  result?: LiveRepointResult
}

// Frontend PRD 11 - repoints the live extraction API to a published bundle
// version. No `docs/design-prds/` Figma brief exists for this screen (see
// the PRD's own "No design source" section) - this UI/copy is a placeholder
// informed directly by PublishModal's pattern, flagged for a real design
// pass before this ships to Kashif, not treated as Figma-grounded certainty.
//
// Built on the generic Modal (not ConfirmModal) for the same reason
// PublishModal is: pending/error/success states ConfirmModal doesn't
// support. Simpler than PublishModal - no textarea, no bumpPatch-style
// client-side version math, since the target version already concretely
// exists (read straight from the `['bundles']` cache).
export function GoLiveModal({
  open,
  targetVersion,
  onConfirm,
  onCancel,
  isPending,
  isError,
  error,
  isSuccess,
  result,
}: GoLiveModalProps) {
  function handleClose() {
    if (!isPending) onCancel()
  }

  if (isSuccess && result) {
    return (
      <Modal open={open} title="Live" onClose={handleClose}>
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <span className="text-title font-semibold text-success">{result.version} is now live</span>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      title="Go live"
      onClose={handleClose}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-control border border-border px-4 py-2 text-body font-medium text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="flex items-center gap-1.5 rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending && <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />}
            {isPending ? 'Going live…' : 'Go Live'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-meta text-text-secondary">Version</span>
          <span className="text-header font-medium text-text-primary">{targetVersion ?? '-'}</span>
        </div>

        {isError && (
          <Banner
            variant="danger"
            icon={AlertCircle}
            title={apiErrorMessage(error) ?? 'Go live failed'}
          >
            {/* No "still saved as a draft" reassurance here (PublishModal has
                one) - repointing never touches the draft, so that claim would
                be actively misleading; state plainly what actually held. */}
            The live version is unchanged.
          </Banner>
        )}
      </div>
    </Modal>
  )
}
